<template>
  <form @submit.prevent :novalidate="true">
    <div class="col-1 ms-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>

    <b-card>
      <template v-slot:header>
        <div class="mb-3 row mb-0">
          <div class="col-lg-6">
            <treeselect v-model="surveyValue"
                        :defaultExpandLevel="Infinity"
                        :clearable="false"
                        :placeholder="$t('Webportal.StructureFilter.Placeholder')"
                        :options="structureItems"
                        :disableFuzzyMatching="true"
                        :disable-branch-nodes="true"
                        :disabled="isBusy"
                        @update:modelValue="surveyInput">
            </treeselect>
          </div>
        </div>
      </template>
      <BAccordion v-if="surveySettings && Object.keys(surveySettings).length > 0" class="d-flex flex-column">

        <!-- General Settings -->
        <BAccordionItem class="mb-3" show>
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.Administration.GeneralSettings') }}
            </h5>
          </template>
          <div class="mb-3 row">
            <label for="DefaultTargetGroup" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.DefaultTargetGroup') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.DefaultTargetGroup.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col-lg-5 col-md-7">
              <b-form-select id="DefaultTargetGroup" v-if="survey && survey.TargetGroups"
                v-model="surveySettings.DefaultTargetGroup"
                size="sm"
                :disabled="isBusy">
                <b-form-select-option
                  v-for="targetGroup in survey.TargetGroups"
                  :key="targetGroup.TargetGroup"
                  :value="targetGroup.TargetGroup">
                  {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupName }})
                </b-form-select-option>
              </b-form-select>
            </div>
          </div>

          <hr />

          <!-- ShowCheckDate -->
          <b-row>
            <label for="ShowCheckDate" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckDate') }} ({{ $t('Webportal.Default') }})
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckDate.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckDate')"
                      @input="setSettingObjectValue('ShowCheckDate', $event.target.checked)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckDate')"
                       @input="resetSettingObjectValue('ShowCheckDate', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>
          <b-row v-if="survey && survey.TargetGroups" v-for="targetGroup in survey.TargetGroups" :key="`ShowCheckDate_${targetGroup.TargetGroup}`">
            <label for="ShowCheckDate" class="col-lg-5 col-md-7 col-form-label pt-0 ps-3">
              {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupName }})
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckDate', targetGroup.TargetGroup)"
                      @input="setSettingObjectValue('ShowCheckDate', $event.target.checked, targetGroup.TargetGroup)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <!-- ShowCheckDateForRawData -->
          <b-row class="mt-3">
            <label for="ShowCheckDateForRawData" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckDateForRawData') }} ({{ $t('Webportal.Default') }})
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckDateForRawData.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckDateForRawData')"
                      @input="setSettingObjectValue('ShowCheckDateForRawData', $event.target.checked)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckDateForRawData')"
                       @input="resetSettingObjectValue('ShowCheckDateForRawData', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>
          <b-row v-if="survey && survey.TargetGroups" v-for="targetGroup in survey.TargetGroups" :key="`ShowCheckDateForRawData_${targetGroup.TargetGroup}`">
            <label for="ShowCheckDateForRawData" class="col-lg-5 col-md-7 col-form-label pt-0 ps-3">
              {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupName }})
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckDateForRawData', targetGroup.TargetGroup)"
                      @input="setSettingObjectValue('ShowCheckDateForRawData', $event.target.checked, targetGroup.TargetGroup)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <!-- ShowCheckTime -->
          <b-row class="mt-3">
            <label for="ShowCheckTime" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckTime') }} ({{ $t('Webportal.Default') }})
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckTime.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckTime')"
                      @input="setSettingObjectValue('ShowCheckTime', $event.target.checked)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckTime')"
                       @input="resetSettingObjectValue('ShowCheckTime', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>
          <b-row v-if="survey && survey.TargetGroups" v-for="targetGroup in survey.TargetGroups" :key="`ShowCheckTime_${targetGroup.TargetGroup}`">
            <label for="ShowCheckTime" class="col-lg-5 col-md-7 col-form-label pt-0 ps-3">
              {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupName }})
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckTime', targetGroup.TargetGroup)"
                      @input="setSettingObjectValue('ShowCheckTime', $event.target.checked, targetGroup.TargetGroup)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <!-- ShowCheckTimeForRawData -->
          <b-row class="mt-3">
            <label for="ShowCheckTimeForRawData" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckTimeForRawData') }} ({{ $t('Webportal.Default') }})
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckTimeForRawData.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckTimeForRawData')"
                      @input="setSettingObjectValue('ShowCheckTimeForRawData', $event.target.checked)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckTimeForRawData')"
                       @input="resetSettingObjectValue('ShowCheckTimeForRawData', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>
          <b-row v-if="survey && survey.TargetGroups" v-for="targetGroup in survey.TargetGroups" :key="`ShowCheckTimeForRawData_${targetGroup.TargetGroup}`">
            <label for="ShowCheckTimeForRawData" class="col-lg-5 col-md-7 col-form-label pt-0 ps-3">
              {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupName }})
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingObjectValue('ShowCheckTimeForRawData', targetGroup.TargetGroup)"
                      @input="setSettingObjectValue('ShowCheckTimeForRawData', $event.target.checked, targetGroup.TargetGroup)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <!-- Checkstatus -->
          <b-row class="mb-3 mt-3">
            <label for="ShowCheckStatus" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckStatus') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckStatus.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingValue('ShowCheckStatus')"
                      @change="v$.surveySettings.ShowCheckStatus.$touch()"
                      @input="setSettingValue('ShowCheckStatus', $event.target.checked)"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckStatus')"
                       @change="v$.surveySettings.ShowCheckStatus.$touch()"
                       @input="resetSettingValue('ShowCheckStatus', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="ShowOverallScore" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowOverallScore') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowOverallScore.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowOverallScore"
                        @change="v$.surveySettings.ShowOverallScore.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="SurveyKpiId1" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyKpi1') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyKpi.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="surveySettings.SurveyKpiId1"
                          @update:model-value="v$.surveySettings.SurveyKpiId1.$touch()"
                          :key="treeKey"
                          :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                          :options="kpiItems"
                          :disabled="isBusy"
                          :disableFuzzyMatching="true"
                          :flat="true"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1">
              </treeselect>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="SurveyKpiId2" class="col-lg-5 col-md-7 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyKpi2') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyKpi.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="surveySettings.SurveyKpiId2"
                          @update:model-value="v$.surveySettings.SurveyKpiId2.$touch()"
                          :key="treeKey"
                          :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                          :options="kpiItems"
                          :disabled="isBusy"
                          :disableFuzzyMatching="true"
                          :flat="true"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1">
              </treeselect>
            </div>
          </b-row>
        </BAccordionItem>

        <!-- Score Calculation -->
        <BAccordionItem class="mb-3">
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.Administration.ScoreCalculation') }}
            </h5>
          </template>

          <!-- Overall calculation type -->
          <b-row class="mb-3">
            <label for="OverallCalculationType" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.OverallCalculationType') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="OverallCalculationType"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.OverallCalculationType"
                                  :disabled="isBusy"
                                  @change="overallCalculationTypeChanged">
                <b-form-radio :value="null">{{ $t('Webportal.Administration.OverallCalculationType.Client') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Client.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="0">{{ $t('Webportal.Administration.OverallCalculationType.Standard') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Standard.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.OverallCalculationType.Weighted') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Weighted.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.OverallCalculationType.Average') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Average.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </b-row>

          <!-- Show overall calculation type description -->
          <b-row class="mb-3">
            <label for="ShowOverallCalculationTypeDescription" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowOverallCalculationTypeDescription') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowOverallCalculationTypeDescription.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowOverallCalculationTypeDescription"
                      @change="v$.surveySettings.ShowOverallCalculationTypeDescription.$touch()"
                      :disabled="isBusy || surveySettings.OverallCalculationType === undefined || surveySettings.OverallCalculationType === null">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <!-- Overall dynamic weighting -->
          <b-row class="mb-3">
            <label for="OverallCalculationDynamicWeighting" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.OverallCalculationDynamicWeighting') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="OverallCalculationDynamicWeighting"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.OverallCalculationDynamicWeighting"
                                  @change="v$.surveySettings.OverallCalculationDynamicWeighting.$touch()"
                                  :disabled="isBusy || surveySettings.OverallCalculationType !== 1">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Off') }}</b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Factor') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Factor.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Percent') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Percent.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </b-row>

          <hr />

          <!-- Scope of previous waves -->
          <b-row class="mb-3">
            <label for="ScopeOfPreviousWavesForCheckReport" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="ScopeOfPreviousWavesForCheckReport"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.ScopeOfPreviousWavesForCheckReport"
                                  @change="v$.surveySettings.ScopeOfPreviousWavesForCheckReport.$touch()"
                                  :disabled="isBusy">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Survey') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Survey.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Project') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Project.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" class="ms-2" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="NumberOfPreviousWavesForCheckReport" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.NumberOfPreviousWavesForCheckReport') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.NumberOfPreviousWavesForCheckReport.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="NumberOfPreviousWavesForCheckReport"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.NumberOfPreviousWavesForCheckReport"
                                  @change="v$.surveySettings.NumberOfPreviousWavesForCheckReport.$touch()"
                                  :options="previousWavesOptions"
                                  :disabled="isBusy" />
            </div>
          </b-row>
        </BAccordionItem>

        <!-- Score Thresholds -->
        <BAccordionItem class="mb-3">
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.Administration.ScoreThresholds') }}
               <span  v-b-tooltip.hover.nofade="toolTipDelay"
                      :title="$t('Webportal.Administration.ScoreThresholds.Description')"
                      class="ms-2">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </h5>
          </template>
          <b-row class="mb-3 align-items-center">
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSettings') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       v-model="useCustomerThresholds"
                       @change="v$.surveySettings.UseCustomerThresholds.$touch()">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>
          <b-row class="mb-3">
            <div class="col">
              <threshold-selector v-model="surveyThresholds"
                                  @change="v$.surveySettings.Thresholds.$touch()" />
            </div>
          </b-row>
        </BAccordionItem>

        <!-- Check Report -->
        <BAccordionItem class="mb-3">
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.CheckReport') }}
            </h5>
          </template>

          <b-row class="mb-3">
            <label for="ShowCheckReportThresholdLegend" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportThresholdLegend') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportThresholdLegend.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportThresholdLegend"
                        @change="v$.surveySettings.ShowCheckReportThresholdLegend.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="ShowCheckReportClusterOverview" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportClusterOverview') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterOverview.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportClusterOverview"
                        @change="v$.surveySettings.ShowCheckReportClusterOverview.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="ShowCheckReportWaveCompare" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportWaveCompare') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportWaveCompare.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportWaveCompare"
                        @change="v$.surveySettings.ShowCheckReportWaveCompare.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="ShowCheckReportKpiOverview" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportKpiOverview') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportKpiOverview.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportKpiOverview"
                        @change="v$.surveySettings.ShowCheckReportKpiOverview.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <hr />

          <b-row class="mb-3">
            <label for="ShowCheckReportClusterScore" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportClusterScore') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterScore.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportClusterScore"
                        @change="v$.surveySettings.ShowCheckReportClusterScore.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="ShowCheckReportClusterPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportClusterPoints') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterPoints.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportClusterPoints"
                        @change="v$.surveySettings.ShowCheckReportClusterPoints.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="ShowCheckReportQuestionPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportQuestionPoints') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportQuestionPoints.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        v-model="surveySettings.ShowCheckReportQuestionPoints"
                        @change="v$.surveySettings.ShowCheckReportQuestionPoints.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>

          <hr />

          <b-row class="mb-3">
            <label class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.InfoPage') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.InfoPage.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <image-uploader ref="infoPageUploader"
                              :value="getInfoPageUrl()"
                              @input="setInfoPageUrl($event)"
                              @value-changed="v$.surveySettings.InfoPageUrl.$touch()"
                              :upload-url="`api/administration/upload-info-page?survey-id=${surveySettings.SurveyId}`"
                              :direct-mode="false"
                              :previewHeight="150" />
            </div>
          </b-row>
        </BAccordionItem>

        <!-- Check Revision -->
        <BAccordionItem class="mb-3">
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.Actions.SurveyRevision') }}
            </h5>
          </template>
          <b-row class="mb-3">
            <label for="SurveyRevisionCreationLimitInDays" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionCreationLimitInDays') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionCreationLimitInDays.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col d-inline-flex align-items-center">
              <dx-number-box v-model="surveySettings.SurveyRevisionCreationLimitInDays"
                              @value-changed="v$.surveySettings.SurveyRevisionCreationLimitInDays.$touch()"
                              class="custom-control-inline" width="8em" height="2em"
                              @input="void 0"
                              format="##0" :show-spin-buttons="true"
                              :step="1" :min="1" :max="999"
                              :show-clear-button="true"
                              :disabled="isBusy" />
              <span class="ms-2">{{ surveySettings.SurveyRevisionCreationLimitInDays > 1 ? $t('Webportal.NotificationSettings.Days') : $t('Webportal.NotificationSettings.Day') }}</span>
            </div>
          </b-row>

          <b-row class="mb-3">
            <label for="SurveyRevisionOnlyQuestionsWithScoring" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionOnlyQuestionsWithScoring') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionOnlyQuestionsWithScoring.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        :checked="getSettingValue('SurveyRevisionOnlyQuestionsWithScoring')"
                        @change="v$.surveySettings.SurveyRevisionOnlyQuestionsWithScoring.$touch()"
                        @input="setSettingValue('SurveyRevisionOnlyQuestionsWithScoring', $event.target.checked)"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ms-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input  class="switch-input" type="checkbox"
                        :checked="useClientSetting('SurveyRevisionOnlyQuestionsWithScoring')"
                        @change="v$.surveySettings.SurveyRevisionOnlyQuestionsWithScoring.$touch()"
                        @input="resetSettingValue('SurveyRevisionOnlyQuestionsWithScoring', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </b-row>
        </BAccordionItem>

        <!-- KPI Question Assignment -->
        <BAccordionItem class="mb-3" :header="$t('Webportal.KpiQuestionAssignment')" header-class="h5">
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.KpiQuestionAssignment') }}
            </h5>
          </template>
          <kpi-question-assignment  :surveyId="surveySettings.SurveyId"
                                    :disabled="isBusy">
          </kpi-question-assignment>
        </BAccordionItem>
      </BAccordion>

      <template v-slot:footer>
        <div class="float-end">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormChanged || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" class="me-2" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="button" @click="submit" class="btn btn-primary" :disabled="!isFormChanged || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" class="me-2" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveysService from '@/services/SurveysService';
import AdministrationService from '@/services/AdministrationService';
import EventBus from '@/services/EventBus.js';
import CollapsiblePanel from '@/components/CollapsiblePanel';
import ThresholdSelector from './ThresholdSelector';
import ImageUploader from './ImageUploader.vue'
import { DxNumberBox } from 'devextreme-vue';
import KpiQuestionAssignment from './KpiQuestionAssignment.vue';

import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import { BButton } from 'bootstrap-vue-next';

export default {
  name: "SurveySettings",

  mixins: [ AppConfig ],

  components: {
    CollapsiblePanel,
    ThresholdSelector,
    ImageUploader,
    DxNumberBox,
    KpiQuestionAssignment
  },

  setup () {
    return { v$: useVuelidate() }
  },

  validations() {
    // Fallback, falls survey oder survey.TargetGroups noch nicht geladen sind
    const targetGroups = this.survey?.TargetGroups || [];
    // Erzeuge ein Objekt mit allen TargetGroups als Keys
    const showCheckDatePerGroup = {};
    targetGroups.forEach(tg => {
      showCheckDatePerGroup[`ShowCheckDate_${tg.TargetGroup}`] = { /* z.B. required, aber hier nur für dirty checks */ };
    });
    const showCheckDateForRawDataPerGroup = {};
    targetGroups.forEach(tg => {
      showCheckDateForRawDataPerGroup[`ShowCheckDateForRawData_${tg.TargetGroup}`] = { /* z.B. required, aber hier nur für dirty checks */ };
    });
    const showCheckTimePerGroup = {};
    targetGroups.forEach(tg => {
      showCheckTimePerGroup[`ShowCheckTime_${tg.TargetGroup}`] = { /* z.B. required, aber hier nur für dirty checks */ };
    });
    const showCheckTimeForRawDataPerGroup = {};
    targetGroups.forEach(tg => {
      showCheckTimeForRawDataPerGroup[`ShowCheckTimeForRawData_${tg.TargetGroup}`] = { /* z.B. required, aber hier nur für dirty checks */ };
    });
    return {
      surveySettings: {
        DefaultTargetGroup: { },
        ShowCheckDate: { },
        ...showCheckDatePerGroup,
        ShowCheckDateForRawData: { },
        ...showCheckDateForRawDataPerGroup,
        ShowCheckTime: { },
        ...showCheckTimePerGroup,
        ShowCheckTimeForRawData: { },
        ...showCheckTimeForRawDataPerGroup,
        ShowCheckStatus: { },
        ShowOverallScore: { },
        SurveyKpiId1: { },
        SurveyKpiId2: { },
        OverallCalculationType: { required },
        ShowOverallCalculationTypeDescription: { },
        OverallCalculationDynamicWeighting: { required },
        ScopeOfPreviousWavesForCheckReport: { required },
        NumberOfPreviousWavesForCheckReport: { required },
        Thresholds: { },
        ShowCheckReportThresholdLegend: { },
        ShowCheckReportClusterOverview: { },
        ShowCheckReportWaveCompare: { },
        ShowCheckReportKpiOverview: { },
        ShowCheckReportClusterScore: { },
        ShowCheckReportClusterPoints: { },
        ShowCheckReportQuestionPoints: { },
        InfoPageUrl: { required },
        SurveyRevisionCreationLimitInDays: {
          required,
          integer: helpers.withMessage(this.$t('Webportal.Validation.Integer'), value => Number.isInteger(value)),
          minValue: helpers.withMessage(this.$t('Webportal.Validation.MinValue', { min: 1 }), value => value >= 1),
          maxValue: helpers.withMessage(this.$t('Webportal.Validation.MaxValue', { max: 999 }), value => value <= 999)
        },
        SurveyRevisionOnlyQuestionsWithScoring: { required },

        UseCustomerThresholds: { }
      }
    };
  },

  data () {
    return {
      treeKey: 0,

      // Filter properties
      structureItems: [],                // Data items for structure treeselect
      surveyValue: null,                 // Selected survey item

      surveySettings: {},
      kpiItems: [],

      surveyThresholds: null,

      isBusy: true,

      previousWavesOptions: [
        { text: '1', value: 1 },
        { text: '2', value: 2 },
        { text: '3', value: 3 },
        { text: '4', value: 4 },
        { text: '5', value: 5 }
      ]
    }
  },

  mounted() {
    this.initializeTheme();
  },

  watch: {
    surveySettings (newVal, oldVal) {
      this.surveyThresholds =
        newVal?.Thresholds?.map(x => { return { value: { startValue: x.Minimum, endValue: x.Maximum }, color: x.Color, icons: x.Icons }});
    },

    surveyThresholds: {
      handler: function(newVal, oldVal) {
        if (this.surveySettings == null)
          return;

        this.surveySettings.Thresholds =
          newVal?.map(x => { return { ScoreId: -1, Minimum: x.value.startValue, Maximum: x.value.endValue, Color: x.color, Icons: x.icons }});
      }, deep: true
    }
  },

  computed: {
    isFormChanged() {
      return this.v$.$anyDirty;
    },

    isFormValid() {
      return !this.v$.$error && this.v$.$anyDirty;
    },

    surveys() {
      // Returns an array of all surveys recursively for the given node
      const returnSurveys = (obj) => {
          return _.concat(_.startsWith(obj.id, 'S') ? obj : [],
              obj.children ? _.flatMapDeep(obj.children, returnSurveys) : []);
      };

      return _.flatMapDeep(this.structureItems, returnSurveys);
    },

    survey() {
      return this.surveys?.find(ss => ss.id === this.surveyValue);
    },

    useCustomerThresholds: {
      get: function() {
        return this.surveyThresholds === undefined || this.surveyThresholds === null;
      },
      set: function(newValue) {
        if (newValue) {
          this.surveyThresholds = null;
        }
        else {
          this.surveyThresholds =
            this.surveySettings.Client?.Thresholds?.map(x => { return { value: { startValue: x.Minimum, endValue: x.Maximum }, color: x.Color, icons: x.Icons }});
        }
      }
    }
  },

  activated() {
    this.getSurveyStructure();
    this.getSurveySettings();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  deactivated() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  beforeRouteLeave (to, from, next) {
    this.$emit('unsavedChanges', this.isFormChanged, next);
  },

  methods: {
    setInfoPageUrl(value) {
      if (!value) {
        this.surveySettings.InfoPageUrl = null;
      }
      this.v$.surveySettings.InfoPageUrl.$touch();
    },

    getInfoPageUrl() {
      return this.surveySettings.InfoPageUrl
        ? this.surveySettings.InfoPageUrl
        : null;
    },

    localizationChanged(localizationId) {
      this.getSurveyStructure();
    },

    getSettingValue(settingName) {
      return this.surveySettings[settingName] ?? this.surveySettings.Client[settingName];
    },

    setSettingValue(settingName, newValue) {
      console.error(`Setting this.surveySettings.${settingName} to ${newValue}`);

      this.surveySettings[settingName] = newValue;
    },

    getSettingObjectValue(settingName, propertyName) {
      const setting = this.surveySettings[settingName];

      if (setting == null)
        return this.surveySettings.Client[settingName];

      return typeof(setting) === 'object'
        ? setting[propertyName ?? 'default'] ?? setting['default']
        : setting;
    },

    setSettingObjectValue(settingName, newValue, propertyName) {
      let setting = this.surveySettings[settingName];
      console.error(`Setting this.surveySettings.${settingName}(${propertyName}) to ${newValue}`);

      if (setting == null || typeof(setting) !== 'object')
      {
        // Initialize new object
        setting = { default: setting ?? this.surveySettings.Client[settingName] };
        this.survey.TargetGroups?.forEach(tg => setting[tg.TargetGroup] = setting.default);
        this.surveySettings[settingName] = setting;
      }

      if (propertyName == null)
      {
        setting.default = newValue;
        // Set all target groups to the new default value
        this.survey.TargetGroups?.forEach(tg => setting[tg.TargetGroup] = setting.default);
      }
      else
        setting[propertyName] = newValue;

      // Vuelidate dirty setzen:
      // console.error(`TOUCH surveySettings[${settingName}]`, this.v$.surveySettings[settingName]);
      try {
        this.v$.surveySettings[settingName]?.$touch();

      } catch (e) {
        console.error("ERROR accessing this.v$.surveySettings[settingName]:", e);
      }
    },

    useClientSetting(settingName) {
      return this.surveySettings[settingName] === undefined || this.surveySettings[settingName] === null;
    },

    resetSettingValue(settingName, useClientSetting) {
      this.setSettingValue(settingName, useClientSetting ? null : this.surveySettings.Client[settingName]);
    },

    resetSettingObjectValue(settingName, useClientSetting) {
      if (useClientSetting)
        this.setSettingValue(settingName, null);
      else
        this.setSettingObjectValue(settingName, this.surveySettings.Client[settingName]);
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          let items = response.data;
          this.structureItems = items;
          if (!this.surveyValue)
          {
            let firstSurvey = _.first(this.surveys);
            this.surveyInput(firstSurvey ? firstSurvey.id : null);
          }
          this.$nextTick(() => { this.treeKey++; });

          this.v$.$reset();
        })
    },

    getSurveySettings() {
      if (this.surveyValue)
      {
        Promise.all([
          AdministrationService.getSurveySettings(this.surveyValue.substring(1)),
          SurveysService.getSurveyKpis({ surveyIds: [ this.surveyValue.substring(1) ] })
        ])
          .then(([responseSurveySettings, responseSurveyKpis])  => {
            this.isBusy = false;

            this.surveySettings = responseSurveySettings.data ? responseSurveySettings.data[0] : null;
            if (this.surveySettings)
            {
              // Set ShowOverallCalculationTypeDescription to client value when not defined
              this.surveySettings.ShowOverallCalculationTypeDescription =
                this.surveySettings.ShowOverallCalculationTypeDescription ?? this.surveySettings.Client.ShowOverallCalculationTypeDescription;
              this.surveySettings.SurveyKpiId1 = this.surveySettings.SurveyKpiId1
                ? "K" + this.surveySettings.SurveyKpiId1
                : (this.surveySettings.SurveyKpiGroupId1 ? "G" + this.surveySettings.SurveyKpiGroupId1 : null);
              this.surveySettings.SurveyKpiId2 = this.surveySettings.SurveyKpiId2
                ? "K" + this.surveySettings.SurveyKpiId2
                : (this.surveySettings.SurveyKpiGroupId2 ? "G" + this.surveySettings.SurveyKpiGroupId2 : null);

              this.surveySettings.InfoPageUrl =
                this.surveySettings.InfoPageExternalImageId ?
                  process.env.BASE_URL + `api/media/image?id=${this.surveySettings.InfoPageExternalImageId}` : null;
            }

            if (responseSurveyKpis.data && responseSurveyKpis.data.length === 1 && responseSurveyKpis.data[0].children) {
              this.kpiItems = responseSurveyKpis.data[0].children;
              this.kpiItems?.forEach(kpiType => {
                kpiType.isDisabled = true;
              });
            } else {
              this.kpiItems = [];
            }
            this.v$.$reset();
          });
      }
      else
      {
        this.surveySettings = null;
        this.v$.$reset();
      }
    },

    // Called whenever the input of the structure treeselect changes
    surveyInput(value) {
      if (this.surveyValue !== value)
      {
      }

      let currentValue = this.surveyValue;
      this.surveyValue = value;

      this.$emit('unsavedChanges', this.isFormChanged, result => {
        if (result === false) {
          this.surveyValue = currentValue;
        }
        else {
          this.getSurveySettings();
        }
      });
    },

    overallCalculationTypeChanged(overallCalculationType) {
      this.surveySettings.OverallCalculationDynamicWeighting = overallCalculationType == 1 ? 2 : null;
      if (overallCalculationType === undefined || overallCalculationType === null) {
        this.surveySettings.ShowOverallCalculationTypeDescription = this.surveySettings.Client.ShowOverallCalculationTypeDescription;
        this.v$.surveySettings.ShowOverallCalculationTypeDescription.$touch();
      }
      this.v$.surveySettings.OverallCalculationType.$touch();
      this.v$.surveySettings.OverallCalculationDynamicWeighting.$touch();
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormChanged, result => {
        if (result !== false) this.getSurveySettings();
      });
    },

    submit() {
      this.isBusy = true;

      // Clone settings for submission
      let surveySettings = { ... this.surveySettings };

      // Map kpi and kpi groups to corresponding properties
      surveySettings.SurveyKpiId1 = this.surveySettings.SurveyKpiId1?.startsWith("K") ? this.surveySettings.SurveyKpiId1.substring(1) : null;
      surveySettings.SurveyKpiId2 = this.surveySettings.SurveyKpiId2?.startsWith("K") ? this.surveySettings.SurveyKpiId2.substring(1) : null;
      surveySettings.SurveyKpiGroupId1 = this.surveySettings.SurveyKpiId1?.startsWith("G") ? this.surveySettings.SurveyKpiId1.substring(1) : null;
      surveySettings.SurveyKpiGroupId2 = this.surveySettings.SurveyKpiId2?.startsWith("G") ? this.surveySettings.SurveyKpiId2.substring(1) : null;

      // Replace undefined with null
      surveySettings.OverallCalculationType = this.surveySettings.OverallCalculationType ?? null;
      surveySettings.OverallCalculationDynamicWeighting = this.surveySettings.OverallCalculationDynamicWeighting ?? null;

      // Set ShowOverallCalculationTypeDescription to null when overall calculation type is set to client value
      surveySettings.ShowOverallCalculationTypeDescription =
        surveySettings.OverallCalculationType !== null ? surveySettings.ShowOverallCalculationTypeDescription : null;

      surveySettings.Thresholds = surveySettings.Thresholds ?? [ { ScoreId: -1 } ]; // Delete existing thresholds

      AdministrationService.setSurveySettings(surveySettings)
        .then(res => {
          if (this.v$.surveySettings.InfoPageUrl.$dirty)
            this.$refs.infoPageUploader.uploadImage();
        })
        .catch(err => {
          console.error("SAVE FAILED", err)
        })
        .finally(() => {
          this.isBusy = false;
          this.v$.$reset();
        });
    }
  }
}
</script>
